import axios from 'axios';
import { TOKEN_EXPIRED } from '../hooks/useAPIHelper';
import { normaliseAssetRow } from "./assetPagesShape";

/**
 * A customer's whole Assets list from paged files (PORTAL-72), or null to mean "use the old path".
 *
 * WHY IT RETURNS NULL RATHER THAN THROWING. This is an optimisation, and the call it replaces
 * (xServiceGetServiceContractUnits) is the one that has always worked. Anything this path cannot do - a build
 * still running, a page that will not load, a page whose row count disagrees with the manifest, the
 * site not being set up for it - is answered by returning null, and the caller falls back to the old
 * call. The one exception is an expired Pronto token, which the old path would also report, so it is
 * thrown in the same words and the caller's existing handling applies.
 *
 * THE BROWSER FETCHES THE PAGES ITSELF, with no credential: each URL is signed, read-only and lasts
 * about 30 minutes. fetch() is used instead of axios on purpose, so nothing in the portal's axios
 * defaults (an Authorization header, say) can reach the storage account, which would refuse it.
 *
 * NULL BECOMES ''. The old path's XML parser turned an empty element into '', and
 * computePlannedStatus tests StatusCode == '' - so a null from the page files would leave the status
 * unset and the job would not read as Open. Normalised here rather than in every consumer.
 */

const PAGES_URL = '/.netlify/functions/xServiceGetAssetPages';

/** How long to wait for a build that was started by this very request before giving up on it. */
const MAX_BUILD_WAIT_MS = 25000;

interface ManifestFile {
  n: number;
  url: string;
  rows: number;
}

interface Manifest {
  pages: number;
  rows: number;
  files: ManifestFile[];
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** On for a browser that opted in, or for a site built with the flag. Off by default. */
export function assetPagesEnabled(): boolean {
  try {
    if (process.env.ASSET_PAGES === '1') {
      return true;
    }

    return typeof window !== 'undefined' && window.localStorage.getItem('assetPages') === '1';
  } catch (_) {
    return false;
  }
}

async function readPage(file: ManifestFile): Promise<any[]> {
  const reply = await fetch(file.url, { method: 'GET', credentials: 'omit' });

  if (!reply.ok) {
    throw new Error(`page ${file.n} answered HTTP ${reply.status}`);
  }

  const rows = await reply.json();

  if (!Array.isArray(rows)) {
    throw new Error(`page ${file.n} is not a list`);
  }

  return rows;
}

const GetAssetPages = async (token: string, customerId: string): Promise<any[] | null> => {
  const started = Date.now();
  const body = `<Filters><CustomerCode><Like>${customerId}</Like></CustomerCode></Filters>`;

  try {
    let manifest: Manifest | null = null;

    while (manifest === null) {
      const reply = await axios.post(PAGES_URL, body, {
        headers: { 'Content-Type': 'application/xml', 'X-Pronto-Token': token },
        validateStatus: (status) => status === 200 || status === 202,
      });

      if (reply.status === 200) {
        manifest = reply.data as Manifest;
        break;
      }

      // 202: a build is running. Wait if it is likely to finish soon, otherwise let the old path
      // serve this load - the build carries on and the next load finds it.
      const retryMs = Math.max(1, Number(reply.data?.retryAfterSeconds ?? 5)) * 1000;

      if (Date.now() - started + retryMs > MAX_BUILD_WAIT_MS) {
        console.log('Asset pages still building - using the standard path for this load');
        return null;
      }

      await wait(retryMs);
    }

    // A manifest with no pages means the page service holds nothing for this customer (C15 holds AUC116 only
    // so far). That is NOT "this customer has no assets" - answering [] would show a customer an empty list.
    // Fall back and let the old path say what is there.
    if (!manifest.files || manifest.files.length === 0) {
      console.log('Asset pages: nothing held for this customer - using the standard path');
      return null;
    }

    const pages = await Promise.all(manifest.files.map((file) => readPage(file)));

    // The manifest states how many rows each page holds. A page that disagrees was damaged or cut
    // short, and passing it on would show a customer fewer jobs with nothing to say so.
    pages.forEach((rows, index) => {
      if (rows.length !== manifest!.files[index].rows) {
        throw new Error(`page ${manifest!.files[index].n} holds ${rows.length} rows, the manifest says ${manifest!.files[index].rows}`);
      }
    });

    const jobs = pages.flat().map(normaliseAssetRow);

    console.log(`Asset pages: ${jobs.length} rows from ${pages.length} page(s) in ${Date.now() - started} ms`);

    return jobs;
  } catch (err: any) {
    if (err?.response?.status === 401 && err.response.data === 'TokenExpired') {
      throw TOKEN_EXPIRED;
    }

    console.log('Asset pages unavailable - using the standard path:', err?.message ?? err);
    return null;
  }
};

export default GetAssetPages;
